import { Head, Link, useForm } from "@inertiajs/react";
import { ArrowLeft, Ban, ExternalLink, Mail, MapPin, Phone, Save, School, Users } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function Show({ prospect, interest_options }) {
  const { data, setData, patch, processing, errors } = useForm({
    contacted: prospect.contacted,
    do_not_contact: prospect.do_not_contact,
    interest_status: prospect.interest_status,
    notes: prospect.notes || "",
  });

  const submit = (event) => {
    event.preventDefault();
    patch(prospect.update_path, { preserveScroll: true });
  };

  return (
    <AdminLayout
      eyebrow="School prospect"
      title={prospect.name}
      actions={(
        <div className="flex items-center gap-2">
          {prospect.do_not_contact ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-destructive"><Ban className="h-4 w-4" /> Do not contact</span>
          ) : prospect.outreach_mailto && (
            <Button size="sm" asChild>
              <a href={prospect.outreach_mailto}><Mail className="h-4 w-4" /> Draft outreach email</a>
            </Button>
          )}
          <Button variant="outline" size="sm" asChild>
            <Link href={prospect.back_path}><ArrowLeft className="h-4 w-4" /> All prospects</Link>
          </Button>
        </div>
      )}
    >
      <Head title={`Admin · ${prospect.name}`} />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><School className="h-4 w-4 text-primary" /> School details</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-5 sm:grid-cols-2">
              <Detail label="Division" value={prospect.division} />
              <Detail label="Province" value={prospect.province} />
              <Detail label="School code" value={prospect.school_code} />
              <Detail label="Grades" value={prospect.grade_range ? `Grades ${prospect.grade_range}` : null} />
              <Detail label="Students" value={prospect.enrollment == null ? null : prospect.enrollment.toLocaleString()} />
              <Detail label="Address" value={prospect.address} icon={<MapPin className="h-4 w-4" />} className="sm:col-span-2" />
              <Detail label="Phone" value={prospect.phone} href={prospect.phone ? `tel:${prospect.phone}` : null} icon={<Phone className="h-4 w-4" />} />
              <Detail label="School email" value={prospect.school_email} href={prospect.school_email ? `mailto:${prospect.school_email}` : null} icon={<Mail className="h-4 w-4" />} />
              <Detail label="Website" value={prospect.website} href={prospect.website} external />
              <Detail label="Research source" value={prospect.source} className="sm:col-span-2 break-words" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Outreach contacts</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 sm:grid-cols-3">
              <ContactGroup
                title="School council"
                name={prospect.school_council_chair}
                email={prospect.school_council_email}
                url={prospect.school_council_url}
              />
              <ContactGroup
                title="Parent society"
                name={prospect.parent_society_name}
                email={prospect.parent_society_contact}
                url={prospect.parent_society_url}
              />
              <ContactGroup
                title="Principal"
                name={prospect.principal_name}
                email={prospect.principal_email}
              />
            </CardContent>
          </Card>
        </div>

        <Card className="h-fit xl:sticky xl:top-8">
          <CardHeader>
            <CardTitle>Outreach status</CardTitle>
            <p className="text-sm text-muted-foreground">Keep this lightweight—status and a useful note are enough.</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-5">
              <label className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.contacted}
                  onChange={(event) => setData("contacted", event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-input text-primary focus:ring-ring"
                />
                <span>
                  <span className="block text-sm font-medium text-foreground">We contacted this school</span>
                  {prospect.contacted_at && (
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {new Date(prospect.contacted_at).toLocaleDateString()}
                      {prospect.contacted_by_name ? ` · ${prospect.contacted_by_name}` : ""}
                    </span>
                  )}
                </span>
              </label>

              <label className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.do_not_contact}
                  onChange={(event) => setData("do_not_contact", event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-input text-primary focus:ring-ring"
                />
                <span>
                  <span className="block text-sm font-medium text-foreground">Do not contact</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">Hides the outreach email for this school.</span>
                </span>
              </label>

              <label className="block">
                <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Interest</span>
                <select
                  value={data.interest_status}
                  onChange={(event) => setData("interest_status", event.target.value)}
                  className="h-11 w-full rounded-lg border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {interest_options.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Notes</span>
                <textarea
                  value={data.notes}
                  onChange={(event) => setData("notes", event.target.value)}
                  rows={8}
                  maxLength={5000}
                  placeholder="Who replied, next step, useful context…"
                  className="w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                {errors.notes && <p className="text-xs text-destructive mt-1">{errors.notes}</p>}
              </label>

              <Button type="submit" className="w-full" disabled={processing}>
                <Save className="h-4 w-4" /> {processing ? "Saving…" : "Save changes"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

function Detail({ label, value, href, external = false, icon, className = "" }) {
  return (
    <div className={className}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
      {!value ? <p className="text-sm text-muted-foreground">Not available</p> : href ? (
        <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline break-all">
          {icon}{value}{external && <ExternalLink className="h-3.5 w-3.5 shrink-0" />}
        </a>
      ) : <p className="flex items-start gap-1.5 text-sm text-foreground">{icon}{value}</p>}
    </div>
  );
}

function ContactGroup({ title, name, email, url }) {
  const emailIsAddress = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || "");
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">{title}</p>
      {name && <p className="text-sm font-medium text-foreground mb-1">{name}</p>}
      {email && (emailIsAddress ? (
        <a href={`mailto:${email}`} className="block text-sm text-primary hover:underline break-all">{email}</a>
      ) : <p className="text-sm text-foreground break-words">{email}</p>)}
      {url && (
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2">
          Contact page <ExternalLink className="h-3 w-3" />
        </a>
      )}
      {!name && !email && !url && <p className="text-sm text-muted-foreground">Not found</p>}
    </div>
  );
}
